import { FormComponentProps } from 'antd/lib/form'
import gql from 'graphql-tag'
import Raven from 'raven-js'
import * as React from 'react'
import styled from 'styled-components'
import { Mutation, compose } from 'react-apollo'
import Box from 'web-makeup/dist/Box'
import Form from 'web-makeup/dist/Form'
import Notification from 'web-makeup/dist/Notification'
import Spin from 'web-makeup/dist/Spin'
import Radio from 'web-makeup/dist/Radio'

import {
  GetProducerProfile,
  UpdateProducer,
  BlockProducer,
  UnblockProducer,
  UpdateProducerStatus,
} from '../../typings/types'
import ProducerProfile from '../ProducerProfile'

const RadioGroup = Radio.Group

const StyledBox = styled(Box)`
  max-width: 100%;
  padding: 2rem 0;

  border: none;

  @media (min-width: 40rem) {
    border-left: none;
    border-right: none;
  }
`

const StyledRadioBox = styled(Box)`
  border: none;
  margin-top: 1.5rem;

  @media (min-width: 40rem) {
    border-left: none;
    border-right: none;
  }

  > .ant-radio-group {
    width: 100%;

    > .ant-radio-wrapper {
      display: block;
      padding: 1rem 0;
      border-top: 1px solid #e8e8e8;

      &:last-child {
        border-bottom: 1px solid #e8e8e8;
      }
    }
  }
`

const UPDATE_PRODUCER = gql`
  mutation updateProducer($userUuid: String!, $user: UpdateUserInput!) {
    updateUser(userUuid: $userUuid, user: $user) {
      avatar {
        cloudinaryId
        cloudinaryType
      }
      name
      email
      phoneNumber
      location {
        city
        state
        country
        googlePlaceId
      }
      dateOfBirth
      gender
      facebookProfile
      linkedinProfile
      twitterProfile
      nickname
      skypeUsername
      uuid
    }
  }
`

const UPDATE_PRODUCER_STATUS = gql`
  mutation updateProducerStatus($userUuid: ID, $producerProfile: ProducerProfileInput) {
    updateProducerProfile(userUuid: $userUuid, producerProfile: $producerProfile) {
      status
    }
  }
`

const BLOCK_PRODUCER = gql`
  mutation blockProducer($userUuid: ID!, $appUuid: ID!) {
    lockAppUser(userUuid: $userUuid, appUuid: $appUuid) {
      success
    }
  }
`

const UNBLOCK_PRODUCER = gql`
  mutation unblockProducer($userUuid: ID!, $appUuid: ID!) {
    unlockAppUser(userUuid: $userUuid, appUuid: $appUuid) {
      success
    }
  }
`

class UpdateProducerMutation extends Mutation<UpdateProducer.Mutation, UpdateProducer.Variables> {}

class BlockProducerMutation extends Mutation<BlockProducer.Mutation, BlockProducer.Variables> {}

class UnblockProducerMutation extends Mutation<
  UnblockProducer.Mutation,
  UnblockProducer.Variables
> {}

class UpdateProducerStatusMutation extends Mutation<
  UpdateProducerStatus.Mutation,
  UpdateProducerStatus.Variables
> {}

interface OwnProps {
  data: GetProducerProfile.GetProducerProfile
  loading: boolean
}

type Props = OwnProps & FormComponentProps

export class ProducerDetails extends React.Component<Props> {
  constructor(props: Props) {
    super(props)

    this.handleLogoUploadSuccess = this.handleLogoUploadSuccess.bind(this)
    this.handleLogoUploadError = this.handleLogoUploadError.bind(this)
    this.handleFormSubmit = this.handleFormSubmit.bind(this)
    this.handleLocationSelect = this.handleLocationSelect.bind(this)
    this.handleLocationChange = this.handleLocationChange.bind(this)
    this.handleBlock = this.handleBlock.bind(this)
    this.handleUnblock = this.handleUnblock.bind(this)
    this.handleProducerStatusUpdate = this.handleProducerStatusUpdate.bind(this)
  }

  handleLogoUploadSuccess(cloudinaryId: string, uploadFor: 'user') {
    const typeField = `${uploadFor}.avatar`
    this.props.form.setFieldsValue({
      [typeField]: {
        cloudinaryType: 'upload',
        cloudinaryId,
      },
    })
  }

  handleLogoUploadError(uploadFor: 'user') {
    return (error: Error) => {
      this.props.form.setFieldsValue({
        [`${uploadFor}.avatar`]: undefined,
      })
    }
  }

  async handleLocationSelect(value: string, options: { props: { children: {} } }) {
    this.props.form.setFieldsValue({
      'user.googlePlaceId': value,
    })
  }

  handleLocationChange(value: string) {
    if (value && value.length) {
      return
    }

    this.props.form.setFieldsValue({
      'user.googlePlaceId': undefined,
    })
  }

  handleFormSubmit(updateUser: (o) => void) {
    this.props.form.validateFields(async (err, values: UpdateProducer.Variables) => {
      if (err) {
        return
      }

      try {
        const user = {
          ...values.user,
          dateOfBirth: values.user.dateOfBirth ? values.user.dateOfBirth.toISOString() : null,
        }

        const userUuid = this.props.data.user.uuid

        await updateUser({
          variables: {
            userUuid,
            user,
          },
        })

        Notification.success({
          message: 'Successfully updated',
          description: 'The profile has been successfully updated',
        })
      } catch (error) {
        Raven.captureException(error)
      }
    })
  }

  async handleBlock(blockProducer) {
    try {
      await blockProducer({
        variables: {
          userUuid: this.props.data.user.uuid,
          appUuid: window.env.PRODUCER_APP_ID,
        },
      })
      Notification.success({
        message: 'Successfully blocked',
        description: 'The profile has been successfully blocked',
      })
    } catch (err) {
      Raven.captureException(err)
    }
  }

  async handleUnblock(unblockProducer) {
    try {
      await unblockProducer({
        variables: {
          userUuid: this.props.data.user.uuid,
          appUuid: window.env.PRODUCER_APP_ID,
        },
      })
      Notification.success({
        message: 'Successfully unblocked',
        description: 'The profile has been successfully unblocked',
      })
    } catch (err) {
      Raven.captureException(err)
    }
  }

  async handleProducerStatusUpdate(e, updateStatus) {
    try {
      await updateStatus({
        variables: {
          userUuid: this.props.data.user.uuid,
          producerProfile: {
            status: e.target.value,
          },
        },
      })
      Notification.success({
        message: 'Successfully updated',
        description: 'The status has been successfully updated',
      })
    } catch (err) {
      Raven.captureException(err)
    }
  }

  render() {
    const { form, data: producerData, loading } = this.props

    return (
      <StyledBox>
        <Spin spinning={loading}>
          <UpdateProducerMutation mutation={UPDATE_PRODUCER}>
            {(updateUser, { loading: updating }) => (
              <BlockProducerMutation
                mutation={BLOCK_PRODUCER}
                refetchQueries={['getProducerProfile']}
              >
                {(blockProducer, { loading: blocking }) => (
                  <UnblockProducerMutation
                    mutation={UNBLOCK_PRODUCER}
                    refetchQueries={['getProducerProfile']}
                  >
                    {(unblockProducer, { loading: unblocking }) => (
                      <ProducerProfile
                        onFormSubmit={e => {
                          e.preventDefault()
                          this.handleFormSubmit(updateUser)
                        }}
                        onBlock={() => this.handleBlock(blockProducer)}
                        onUnblock={() => this.handleUnblock(unblockProducer)}
                        handleLocationChange={this.handleLocationChange}
                        handleLocationSelect={this.handleLocationSelect}
                        handleLogoUploadError={this.handleLogoUploadError}
                        handleLogoUploadSuccess={this.handleLogoUploadSuccess}
                        updating={updating}
                        blocking={blocking}
                        unblocking={unblocking}
                        producer={producerData}
                        form={form}
                      />
                    )}
                  </UnblockProducerMutation>
                )}
              </BlockProducerMutation>
            )}
          </UpdateProducerMutation>
        </Spin>
        <UpdateProducerStatusMutation
          mutation={UPDATE_PRODUCER_STATUS}
          refetchQueries={['getProducerProfile']}
        >
          {(updateStatus, { loading: updating }) => (
            <Spin spinning={updating}>
              <StyledRadioBox>
                <RadioGroup
                  onChange={e => this.handleProducerStatusUpdate(e, updateStatus)}
                  value={producerData && producerData.status}
                >
                  <Radio value="ONLINE">Online - All</Radio>
                  <Radio value="PREFERRED_ONLY">Online - Preferred</Radio>
                  <Radio value="OFFLINE">Offline</Radio>
                </RadioGroup>
              </StyledRadioBox>
            </Spin>
          )}
        </UpdateProducerStatusMutation>
      </StyledBox>
    )
  }
}

export default compose(Form.create())(ProducerDetails)



// WEBPACK FOOTER //
// ./src/components/ProducerDetails/ProducerDetails.tsx